import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock } from 'lucide-react';

function fmtDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return d; }
}

/**
 * PlanShareCard — Instagram-style rich plan card rendered inside a chat bubble.
 * Tap to open the plan; "Join" CTA jumps straight to the plan detail page.
 */
export default function PlanShareCard({ planShare, isMine }) {
  const navigate = useNavigate();
  if (!planShare) return null;
  const deleted = planShare.deleted || !planShare.title;
  const spots = planShare.spots_remaining;
  const loc = [planShare.venue, planShare.city].filter(Boolean).join(', ');
  const timeBits = [planShare.date && fmtDate(planShare.date), planShare.start_time].filter(Boolean).join(' · ');

  if (deleted) {
    return (
      <div className={`rounded-xl mb-1 px-3 py-2.5 ${isMine ? 'bg-[#3F5200]/10' : 'bg-[#F1F0EA]'} border border-black/[0.04]`} data-testid="plan-card-deleted">
        <p className="text-xs italic text-[#9B9EA3]">This plan is no longer available.</p>
      </div>
    );
  }

  const go = (e) => { e.stopPropagation(); navigate(`/plan/${planShare.plan_id}`); };

  return (
    <div
      className="rounded-2xl overflow-hidden mb-1 w-[260px] max-w-full bg-white border border-black/[0.06] shadow-sm"
      data-testid={`plan-card-${planShare.plan_id}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Cover */}
      <div className="relative h-[110px] w-full bg-[#F1F0EA]">
        {planShare.cover_image ? (
          <img decoding="async" src={planShare.cover_image} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar size={32} className="text-[#9B9EA3]" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent p-2 pt-8">
          <p className="text-white text-[13px] font-extrabold leading-tight line-clamp-2">{planShare.title}</p>
        </div>
      </div>
      <div className="px-3 py-2.5 space-y-1.5">
        {loc && (
          <div className="flex items-center gap-1.5 text-[#3F3F3F]">
            <MapPin size={12} className="text-[#6B8A00] flex-shrink-0" />
            <span className="text-[11px] truncate">{loc}</span>
          </div>
        )}
        {timeBits && (
          <div className="flex items-center gap-1.5 text-[#3F3F3F]">
            <Clock size={12} className="text-[#6B8A00] flex-shrink-0" />
            <span className="text-[11px] truncate">{timeBits}</span>
          </div>
        )}
        {typeof spots === 'number' && spots >= 0 && (
          <div className="flex items-center gap-1.5 text-[#3F3F3F]">
            <Users size={12} className="text-[#6B8A00] flex-shrink-0" />
            <span className="text-[11px] truncate">
              {spots === 0 ? 'Full' : `${spots} spot${spots === 1 ? '' : 's'} left`}
              {planShare.capacity ? ` · ${planShare.approved_count}/${planShare.capacity}` : ''}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={go}
          data-testid={`plan-card-join-${planShare.plan_id}`}
          className="mt-1.5 w-full py-1.5 rounded-full bg-[#B6E600] text-[#111111] text-xs font-bold active:scale-[0.97] transition-transform"
        >
          {spots === 0 ? 'View plan' : 'Join plan'}
        </button>
      </div>
    </div>
  );
}
